import React, { Fragment, useMemo, useState, useEffect } from "react";
import { Link, useSearchParams } from "wouter";
import { gql, useQuery } from "urql";

import { DateHelper } from "../../../common/src";
import { CustodianPositionType } from "../types.generated";

import { DateForm } from "../../../components/src/dateForm";
import { SelectColumnFilter } from "../../../components/src/react-table/ReactTableFilters";
import { ReactTable } from "../../../components/src/react-table/ReactTable";
import { useQueryState } from "../../../components/src/use-query-state";
import { formatNumberWithSpaces } from "../../../common/src/utils/FormatFunctions";

export const getCustodianPositions = gql`
    query getCustodianPositions(
        $date: GraphQLDateString
        $clientId: GraphQLObjectId
        $idIn: [GraphQLObjectId!]
        $isinIn: [String!]
        $statusIn: [StatusEnum!]
        $typeIn: [CustodianPositionType!]
        $clientIds: [GraphQLObjectId!]
        $instrumentIdIn: [GraphQLObjectId!]
        $startDate: GraphQLDateString
        $endDate: GraphQLDateString
        $include: [CustodianPositionType]
        $filterZeroPositions: Boolean
    ) {
        custodianPositions(
            date: $date
            clientId: $clientId
            idIn: $idIn
            isinIn: $isinIn
            statusIn: $statusIn
            typeIn: $typeIn
            clientIds: $clientIds
            instrumentIdIn: $instrumentIdIn
            startDate: $startDate
            endDate: $endDate
            include: $include
            filterZeroPositions: $filterZeroPositions
        ) {
            _id
            description
            clientId
            client {
                name
            }
            custodianId
            custodian {
                name
            }
            date
            isin
            instrument {
                _id
                name
            }
            accountNumber
            cashAccountNumber
            currency
            price
            quantity
            updateUserId
            createTimestamp
            updateTimestamp
            updateUserInfo {
                name
            }
        }
    }
`;

export const CustodianPositionsPage = (): React.ReactElement => {
    const [date] = useQueryState("date", DateHelper.dateAddDays(new Date(), -1).toISOString().substring(0, 10));

    const [queryArgs] = useSearchParams();

    const [variables, setVariables] = useState(null);

    const [{ fetching, error, data }] = useQuery({
        query: getCustodianPositions,
        variables: variables,
        pause: !queryArgs
    });

    useEffect(() => {
        const instrumentIdIn = queryArgs.get("instrumentIdIn");
        const variables = {
            instrumentIdIn: instrumentIdIn ? [instrumentIdIn] : undefined,
            date: instrumentIdIn ? null : queryArgs.get("date") ? queryArgs.get("date") : date,
            include: [CustodianPositionType.Automatic, CustodianPositionType.Manual]
        };
        setVariables(variables);
    }, [queryArgs, date]);

    const columns = useMemo(
        () => [
            {
                header: "Id",
                accessorKey: "_id",
                cell: (cellProps) => {
                    const { row } = cellProps;
                    return <Link to={`/reconciliation/custodianpositions/${row.original._id}`}>{row.original._id}</Link>;
                },
                size: 30
            },
            {
                header: "Client",
                id: "client.name",
                accessorKey: "client.name",
                cell: (cellProps) => {
                    const { row } = cellProps;
                    return <Link to={`/parties/${row.original.clientId}`}>{cellProps.getValue()}</Link>;
                },
                filter: SelectColumnFilter,
                size: 75
            },
            {
                header: "Custodian",
                id: "custodian.name",
                accessorKey: "custodian.name",
                cell: (cellProps) => {
                    const { row } = cellProps;
                    return <Link to={`/parties/${row.original.custodianId}`}>{cellProps.getValue()}</Link>;
                },
                filter: SelectColumnFilter,
                size: 75
            },
            {
                header: "Instrument",
                id: "instrument.name",
                accessorKey: "instrument.name",
                cell: (cellProps) => {
                    const { row } = cellProps;
                    if (row.original.instrument) {
                        return <Link to={`/instruments/${row.original.instrument._id}`}>{cellProps.getValue()}</Link>;
                    } else {
                        return <div>None</div>;
                    }
                }
            },
            {
                header: "Isin",
                accessorKey: "isin"
            },
            {
                header: "Description",
                accessorKey: "description",
                filter: SelectColumnFilter
            },
            {
                header: "Date",
                accessorKey: "date",
                filter: SelectColumnFilter
            },
            {
                header: "Long name",
                accessorKey: "longName"
            },
            {
                header: "Account number",
                accessorKey: "accountNumber",
                filter: SelectColumnFilter
            },
            {
                header: "Currency",
                accessorKey: "currency",
                filter: SelectColumnFilter
            },
            {
                header: "Price",
                accessorKey: "price",
                filterFn: "startsWith"
            },
            {
                header: "Quantity",
                id: "quantity",
                accessorKey: "quantity",
                filterFn: "startsWith",
                cell: (cellProps) => {
                    return <div style={{ textAlign: "right" }}>{formatNumberWithSpaces(cellProps.getValue())}</div>;
                }
            },
            {
                header: "Created",
                accessorKey: "createTimestamp",
                filter: SelectColumnFilter
            },
            {
                header: "Updated",
                accessorKey: "updateTimestamp",
                filter: SelectColumnFilter,
                size: 75
            },
            {
                header: "Updated by",
                id: "updateUserInfo.name",
                accessorKey: "updateUserInfo.name",
                cell: (cellProps) => {
                    return <span>{cellProps.getValue()}</span>;
                },
                filter: SelectColumnFilter
            }
        ],
        []
    );

    if (fetching) return <p>Loading</p>;
    if (error) return <p>error: {JSON.stringify(error, null, 2)}</p>;

    return (
        <Fragment>
            <div className="flex flex-wrap -mx-2">
                <div className="flex w-full px-2 mb-4">
                    <DateForm defaultDateString={date} dateName={"date"}></DateForm>
                </div>
            </div>
            <div className="flex flex-wrap -mx-2">
                <div className="w-full px-2 mb-4">
                    <Link className="float-right" to={"/reconciliation/custodianpositions/new"}>
                        New
                    </Link>
                </div>
            </div>

            <div className="flex flex-wrap -mx-2">
                <div className="w-full px-2 mb-4">
                    <p>Number of custodian positions: {data.custodianPositions.length}</p>
                    <ReactTable
                        columns={columns}
                        data={data.custodianPositions}
                        defaultHiddenColumns={["createTimestamp"]}
                        exportToXlsxFile="CustodianPositions.xlsx"
                    />
                </div>
            </div>
        </Fragment>
    );
};
